import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  getProductLibraryTemplate,
  type AiUseDraft,
  type ClassificationOption,
  type EntryOptions,
} from './register-api'
import {
  resolveAiUseTechnologyFamily,
  type TechnologyFamilyResolution,
} from './technology-family-api'

type DraftData = Record<string, unknown>

function asObject(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {}
}

function asStrings(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : []
}

function asString(value: unknown) {
  return typeof value === 'string' ? value : ''
}

function nullableString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null
}

function familyDescription(option: ClassificationOption | undefined) {
  if (!option) return null

  const defaultDescriptions: Record<string, string> = {
    predictive: 'Uses information to identify patterns, make a prediction, score something, or recommend an action.',
    generative: 'Creates new content such as text, images, audio, video, or software code from instructions or examples.',
    conversational: 'Lets people interact with an AI through a chat-style conversation or assistant.',
    computer_vision: 'Interprets images, video, or visual documents to identify or classify what is shown.',
    speech_audio: 'Interprets or creates spoken language, audio, or sound.',
    optimisation: 'Finds a preferred option, schedule, allocation, or route from a set of possible choices.',
    other: 'Use this only when none of the listed families fits. Record the closest practical description now and ask a technical owner to confirm it later.',
  }
  const primary = option.helpText ?? defaultDescriptions[option.valueCode]
  const example = option.exampleText ? `For example: ${option.exampleText}` : null
  return [primary, example].filter((part): part is string => Boolean(part)).join(' ')
}

function resolutionMatchesIdentity(
  resolution: Record<string, unknown>,
  productId: string,
  solutionId: string,
) {
  if (!Object.prototype.hasOwnProperty.call(resolution, 'selectedProductId')
      || !Object.prototype.hasOwnProperty.call(resolution, 'selectedSolutionId')) return false

  return nullableString(resolution.selectedProductId) === (productId || null)
    && nullableString(resolution.selectedSolutionId) === (solutionId || null)
}

function resolvedPinFromData(data: DraftData, productId: string, solutionId: string) {
  const candidate = asObject(data.technologyFamilyResolution)
  if (candidate.state !== 'resolved' || !resolutionMatchesIdentity(candidate, productId, solutionId)) return null
  return candidate as TechnologyFamilyResolution
}

function sourceCopy(sourceKind: TechnologyFamilyResolution['sourceKind']) {
  if (sourceKind === 'confirmed_solution') return 'This classification comes from the confirmed organisational Solution.'
  if (sourceKind === 'legacy_template') return 'This classification comes from the governed Product Library information linked to this Product.'
  return 'This classification comes from the Product Library.'
}

export function TechnologyFamilyField({
  organisationId,
  draft,
  data,
  options,
  setData,
}: {
  organisationId: string
  draft: AiUseDraft
  data: DraftData
  options: EntryOptions
  setData: (patch: DraftData) => void
}) {
  const productId = asString(data.productId)
  const solutionId = asString(data.solutionId)
  const privateProductId = asString(data.privateProductId)
  const governedSelection = Boolean(solutionId || (productId && !privateProductId))
  const classifications = asObject(data.classifications)
  const selectedFamily = asStrings(classifications.technology_family)
    .find((code) => code !== 'needs_technical_confirmation') ?? ''
  const familyOptions = (options.classificationSets.technology_family ?? [])
    .filter((option) => option.valueCode !== 'needs_technical_confirmation')
  const selectedOption = familyOptions.find((option) => option.valueCode === selectedFamily)
  const savedResolution = resolvedPinFromData(data, productId, solutionId)

  const snapshotProductId = draft.catalogueSnapshot?.products[0]?.id ?? ''
  const explicitTemplateId = asString(data.productTemplateReferenceId)
  const templateId = !solutionId && productId
    ? explicitTemplateId || (snapshotProductId === productId ? draft.templateId ?? '' : '')
    : ''
  const savedLegacyTemplateVersionId = savedResolution?.sourceKind === 'legacy_template'
    ? savedResolution.versionRef?.templateVersionId ?? null
    : null
  const templateQuery = useQuery({
    queryKey: ['technology-family-template-version', organisationId, templateId, productId],
    queryFn: () => getProductLibraryTemplate(organisationId, templateId),
    enabled: Boolean(governedSelection && organisationId && productId && templateId && !savedLegacyTemplateVersionId),
    retry: false,
  })
  const templateBelongsToProduct = Boolean(templateQuery.data?.products.some((product) => product.id === productId))
  const legacyTemplateVersionId = savedLegacyTemplateVersionId
    ?? (templateBelongsToProduct
      ? templateQuery.data?.templateVersionId ?? null
      : (!explicitTemplateId && snapshotProductId === productId ? draft.templateVersionId : null))
  const templateLookupComplete = Boolean(savedLegacyTemplateVersionId) || !templateId || templateQuery.isSuccess || templateQuery.isError
  const resolutionQuery = useQuery({
    queryKey: [
      'technology-family-resolution',
      draft.id,
      productId,
      solutionId,
      legacyTemplateVersionId,
      savedResolution ? JSON.stringify(savedResolution) : '',
    ],
    queryFn: () => resolveAiUseTechnologyFamily({
      draftId: draft.id,
      productId: productId || null,
      solutionId: solutionId || null,
      legacyTemplateVersionId,
      savedResolution,
    }),
    enabled: Boolean(governedSelection && templateLookupComplete && !templateQuery.isError),
    retry: false,
  })

  useEffect(() => {
    const existingResolution = asObject(data.technologyFamilyResolution)
    const hasExistingResolution = Object.keys(existingResolution).length > 0
    const governedSource = asString(data.technologyFamilySource) === 'product_library'
      || asString(data.technologyFamilySource) === 'confirmed_solution'

    if (!governedSelection) {
      const patch: DraftData = {}
      if (explicitTemplateId) patch.productTemplateReferenceId = ''
      if (hasExistingResolution) patch.technologyFamilyResolution = null
      if (hasExistingResolution || governedSource) {
        patch.classifications = { ...classifications, technology_family: [] }
        patch.technologyFamilySource = ''
      }
      if (Object.keys(patch).length > 0) setData(patch)
      return
    }

    const staleResolution = hasExistingResolution && !resolutionMatchesIdentity(existingResolution, productId, solutionId)
    const staleTemplate = Boolean(solutionId && explicitTemplateId)
    if (staleResolution || staleTemplate) {
      setData({
        ...(staleTemplate ? { productTemplateReferenceId: '' } : {}),
        ...(staleResolution ? {
          classifications: { ...classifications, technology_family: [] },
          technologyFamilyResolution: null,
          technologyFamilySource: '',
        } : {}),
      })
    }
  }, [classifications, data.technologyFamilyResolution, data.technologyFamilySource, explicitTemplateId, governedSelection, productId, setData, solutionId])

  useEffect(() => {
    const resolution = resolutionQuery.data
    if (!governedSelection || !resolution || !resolutionMatchesIdentity(resolution, productId, solutionId)) return

    if (resolution.state === 'resolved' && resolution.familyCode) {
      const currentResolution = asObject(data.technologyFamilyResolution)
      const source = resolution.sourceKind === 'confirmed_solution' ? 'confirmed_solution' : 'product_library'
      if (selectedFamily === resolution.familyCode
          && asString(data.technologyFamilySource) === source
          && JSON.stringify(currentResolution) === JSON.stringify(resolution)) return

      setData({
        classifications: { ...classifications, technology_family: [resolution.familyCode] },
        technologyFamilyResolution: resolution,
        technologyFamilySource: source,
      })
      return
    }

    const currentFamily = asStrings(classifications.technology_family)
    const alreadyPending = currentFamily.length === 1 && currentFamily[0] === 'needs_technical_confirmation'
    if (alreadyPending && !data.technologyFamilyResolution && asString(data.technologyFamilySource) === 'needs_confirmation') return
    setData({
      classifications: { ...classifications, technology_family: ['needs_technical_confirmation'] },
      technologyFamilyResolution: null,
      technologyFamilySource: 'needs_confirmation',
    })
  }, [classifications, data.technologyFamilyResolution, data.technologyFamilySource, governedSelection, productId, resolutionQuery.data, selectedFamily, setData, solutionId])

  if (!governedSelection) {
    const guidance = familyDescription(selectedOption)
    return (
      <fieldset className="technology-family-field">
        <legend>Technology family</legend>
        <label>
          <span>Which best describes this AI?</span>
          <select
            value={selectedFamily}
            onChange={(event) => setData({
              classifications: {
                ...classifications,
                technology_family: event.target.value ? [event.target.value] : ['needs_technical_confirmation'],
              },
              technologyFamilyResolution: null,
              technologyFamilySource: event.target.value ? 'user_selected' : 'needs_confirmation',
            })}
            aria-describedby={guidance ? 'technology-family-guidance' : undefined}
          >
            <option value="">Select one technology family</option>
            {familyOptions.map((option) => <option key={option.valueCode} value={option.valueCode}>{option.label}</option>)}
          </select>
        </label>
        <p className="technology-family-description">Choose the plain-English description that best matches this AI.</p>
        {guidance ? <p className="technology-family-guidance" id="technology-family-guidance" aria-live="polite">{guidance}</p> : null}
      </fieldset>
    )
  }

  const subjectName = asString(data.productDisplayName)
    || draft.catalogueSnapshot?.products.find((product) => product.id === productId)?.name
    || asString(data.solutionName)
    || 'this recognised Product'
  const lookupError = templateQuery.error ?? resolutionQuery.error
  const lookupLoading = (Boolean(templateId) && !savedLegacyTemplateVersionId && templateQuery.isLoading)
    || resolutionQuery.isLoading
    || !templateLookupComplete

  if (lookupLoading) {
    return (
      <section className="technology-family-field technology-family-pending" aria-labelledby="technology-family-heading" aria-live="polite">
        <span id="technology-family-heading">Technology family</span>
        <p>Loading the governed Technology Family for {subjectName}…</p>
      </section>
    )
  }

  if (lookupError) {
    const retry = () => {
      if (templateQuery.isError) {
        void templateQuery.refetch()
        return
      }
      void resolutionQuery.refetch()
    }
    return (
      <section className="technology-family-field technology-family-pending" aria-labelledby="technology-family-heading" role="alert">
        <span id="technology-family-heading">Technology family</span>
        <strong>Technology Family lookup failed</strong>
        <p>We could not confirm the governed classification for {subjectName}. The Product selection has been kept. This is a lookup failure, not missing classification data.</p>
        <button className="button secondary" type="button" onClick={retry}>Retry Technology Family lookup</button>
      </section>
    )
  }

  const resolution = resolutionQuery.data
  if (resolution?.state === 'resolved' && resolution.familyCode) {
    const inheritedOption = familyOptions.find((option) => option.valueCode === resolution.familyCode)
    return (
      <section className="technology-family-field technology-family-inherited" aria-labelledby="technology-family-heading">
        <span id="technology-family-heading">Technology family</span>
        <strong>{inheritedOption?.label ?? resolution.familyCode}</strong>
        {familyDescription(inheritedOption) ? <p>{familyDescription(inheritedOption)}</p> : null}
        <p>{sourceCopy(resolution.sourceKind)} It is inherited here and cannot be changed on this AI use.</p>
      </section>
    )
  }

  if (resolution?.state === 'unavailable_source') {
    return (
      <section className="technology-family-field technology-family-pending" aria-labelledby="technology-family-heading">
        <span id="technology-family-heading">Technology family</span>
        <strong>Governed source unavailable</strong>
        <p>{subjectName} is recognised, but its governed classification source is no longer available. You can save this draft, but the source must be corrected or another Product selected before the AI use can be finalised.</p>
      </section>
    )
  }

  return (
    <section className="technology-family-field technology-family-pending" aria-labelledby="technology-family-heading">
      <span id="technology-family-heading">Technology family</span>
      <strong>Classification needs confirmation</strong>
      <p>{subjectName} is recognised, but its Technology Family has not yet been confirmed in the Product Library. You can save this draft. A technical owner must confirm the classification before this AI use can be finalised.</p>
    </section>
  )
}
